import { useState, useCallback } from "react";
import axios from "axios";
import { auth } from "../../../firebase/firebase";
import { FEEDBACK_API_URL } from "./constants";

/**
 * Custom hook for managing feedback state and submission (thumbs up/down + modal).
 */
export function useFeedback() {
    const [thumbsUpStates, setThumbsUpStates] = useState({});
    const [thumbsDownStates, setThumbsDownStates] = useState({});

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [feedbackReason, setFeedbackReason] = useState("");
    const [selectedMessage, setSelectedMessage] = useState("");
    const [selectedMessageId, setSelectedMessageId] = useState("");
    const [selectedMessageTime, setSelectedMessageTime] = useState("");
    const [selectedSessionId, setSelectedSessionId] = useState("");
    const [isFeedbackSubmitting, setIsFeedbackSubmitting] = useState(false);
    const [feedbackSuccess, setFeedbackSuccess] = useState(false);

    const toggleThumbsUp = useCallback((id) => {
        setThumbsUpStates((prev) => ({ ...prev, [id]: !prev[id] }));
        setThumbsDownStates((prev) => ({ ...prev, [id]: false }));
    }, []);

    const openFeedbackModal = useCallback(
        (sessionId, messageTime, message, messageId) => {
            setIsModalOpen(true);
            // Reset thumbs up for this message when opening thumbs-down modal
            setThumbsUpStates((prev) => ({ ...prev, [messageId]: false }));
            setSelectedMessageTime(messageTime || null);
            setSelectedMessageId(messageId || null);
            setSelectedMessage(message);
            setSelectedSessionId(sessionId);
        },
        [],
    );

    const closeFeedbackModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const submitFeedback = useCallback(async () => {
        setIsFeedbackSubmitting(true);
        setFeedbackSuccess(false);

        const user = auth.currentUser;
        if (!user) {
            alert("Please sign in to submit feedback.");
            setIsFeedbackSubmitting(false);
            return;
        }

        try {
            const token = await user.getIdToken();
            await axios.post(
                FEEDBACK_API_URL,
                {
                    message: selectedMessage,
                    time: selectedMessageTime,
                    session_id: selectedSessionId,
                    reason: feedbackReason,
                    message_id: selectedMessageId || null,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                },
            );

            setFeedbackSuccess(true);
            setThumbsDownStates((prev) => ({
                ...prev,
                [selectedMessageId]: true,
            }));
            setIsModalOpen(false);
            setTimeout(() => setFeedbackSuccess(false), 5000);
        } catch (error) {
            console.error("Error submitting feedback:", error);
        } finally {
            setIsFeedbackSubmitting(false);
        }
    }, [
        selectedMessage,
        selectedMessageTime,
        selectedSessionId,
        feedbackReason,
        selectedMessageId,
    ]);

    return {
        thumbsUpStates,
        thumbsDownStates,
        isModalOpen,
        feedbackReason,
        setFeedbackReason,
        isFeedbackSubmitting,
        feedbackSuccess,
        toggleThumbsUp,
        openFeedbackModal,
        closeFeedbackModal,
        submitFeedback,
    };
}
